<script setup lang="ts">
import { computed, ref, watch } from "vue";

import { toggleActions } from "@/action/toggleActions";
import { getStickyChanges } from "@/backend/read/BackendReader";
import SidePanel from "@/components/SidePanel/SidePanel.vue";
import SidePanelContent from "@/components/SidePanel/SidePanelContent.vue";
import ComplexList from "@/components/ui/ComplexList/ComplexList.vue";
import ListBanner from "@/components/ui/ComplexList/ListBanner.vue";
import ListEmpty from "@/components/ui/ComplexList/ListEmpty.vue";
import SvgIcon from "@/components/ui/SvgIcon/SvgIcon.vue";
import TypeDot from "@/components/ui/TypeDot/TypeDot.vue";
import type { StickyChange } from "@/model/change";
import { useActivityStore } from "@/store/activity";
import { useBoardStore } from "@/store/board";
import { mirrorOriginId } from "@/store/card";

import ChangesByDay from "./internal/ChangesByDay.vue";

const card = computed(() => useActivityStore().card);
const changes = ref<StickyChange[]>([]);
const incomplete = ref(false);

//  Close this panel when the Card loses focus
watch(
  () => useBoardStore().activeCardId,
  (activeId) => {
    if (card.value && activeId !== card.value.id) {
      close();
    }
  },
);

watch(card, async (card) => {
  if (card) {
    const ch = await getStickyChanges(mirrorOriginId(card) || card.id);
    const len = ch.length;
    incomplete.value = len > 0 && ch[len - 1].kind !== "create";
    // if we have an incomplete history, remove the first change
    // as it's an artificial change from "no fields" to "fields at the moment sticky activity was implemented"
    if (incomplete.value) {
      ch.splice(len - 1, 1);
    }
    changes.value = ch;
  }
});

function close() {
  toggleActions.toggleCardHistory("close-button");
}
</script>

<template>
  <div class="sticky-activity-panel" data-ignore-click @scroll.stop>
    <SidePanel side="right" :active="!!card" @close="close">
      <SidePanelContent v-focus-trap v-autofocus :title="$t('cardDiff.cardTitle')">
        <!-- Panel subtitle: card 'icon' and text -->
        <template v-if="card" #sub-title>
          <TypeDot
            size="large"
            :color="card.type.color"
            role="note"
            :aria-label="$t('label.cardDiff.cardType', { type: card.type.name })"
          />
          <span class="text">{{ card.text }}</span>
          <SvgIcon
            v-if="!!mirrorOriginId(card)"
            name="general/mirror"
            width="20"
            height="20"
            role="img"
            :aria-label="$t('label.cardDiff.mirrored')"
          />
        </template>

        <ComplexList>
          <!-- Changes, grouped by day -->
          <ChangesByDay :changes disable-enlarge />

          <!-- Empty List Message -->
          <ListEmpty v-if="changes.length === 0" class="group">
            {{ $t("stickyChange.empty") }}
          </ListEmpty>

          <!-- Incomplete History Banner (for stickies that existed
           before we started saving change history) -->
          <ListBanner v-else-if="incomplete" class="group">
            {{ $t("stickyChange.incomplete") }}
          </ListBanner>
        </ComplexList>
      </SidePanelContent>
    </SidePanel>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/z-index";

.sticky-activity-panel {
  position: relative;
  z-index: z-index.$menu;
}
</style>
